
import { useState, useRef, useEffect } from 'react';

interface ShareNewsButtonProps {
  article: {
    id: string;
    title: string;
    category: string;
    excerpt: string;
  };
}

export default function ShareNewsButton({ article }: ShareNewsButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const articleUrl = `${window.location.origin}${(window as any).__BASE_PATH__ || ''}/news/${article.id}`;
  const shareText = `${article.title} — ${article.excerpt.slice(0, 120)}...`;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const shareOptions = [
    {
      name: 'Facebook',
      icon: 'ri-facebook-fill',
      color: 'text-[#1877F2]',
      bg: 'bg-[#1877F2]/10',
      action: () => {
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(articleUrl)}&quote=${encodeURIComponent(shareText)}`,
          '_blank',
          'width=600,height=400'
        );
      },
    },
    {
      name: 'X (Twitter)',
      icon: 'ri-twitter-x-fill',
      color: 'text-gray-900',
      bg: 'bg-gray-900/10',
      action: () => {
        window.open(
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(articleUrl)}`,
          '_blank',
          'width=600,height=400'
        );
      },
    },
    {
      name: 'LinkedIn',
      icon: 'ri-linkedin-fill',
      color: 'text-[#0A66C2]',
      bg: 'bg-[#0A66C2]/10',
      action: () => {
        window.open(
          `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(articleUrl)}`,
          '_blank',
          'width=600,height=400'
        );
      },
    },
    {
      name: 'WhatsApp',
      icon: 'ri-whatsapp-fill',
      color: 'text-[#25D366]',
      bg: 'bg-[#25D366]/10',
      action: () => {
        window.open(
          `https://wa.me/?text=${encodeURIComponent(shareText + '\n' + articleUrl)}`,
          '_blank'
        );
      },
    },
    {
      name: 'Email',
      icon: 'ri-mail-fill',
      color: 'text-amber-600',
      bg: 'bg-amber-600/10',
      action: () => {
        window.open(
          `mailto:?subject=${encodeURIComponent(article.title + ' — Roseville News')}&body=${encodeURIComponent(shareText + '\n\nRead more: ' + articleUrl)}`,
          '_self'
        );
      },
    },
    {
      name: copied ? 'Copied!' : 'Copy Link',
      icon: copied ? 'ri-check-line' : 'ri-link',
      color: copied ? 'text-teal-600' : 'text-gray-600',
      bg: copied ? 'bg-teal-600/10' : 'bg-gray-600/10',
      action: () => {
        navigator.clipboard.writeText(articleUrl).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        });
      },
    },
  ];

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
      >
        <i className="ri-share-forward-line text-lg"></i>
        Share Article
      </button>

      {isOpen && (
        <div className="absolute left-0 top-12 bg-white rounded-xl shadow-2xl border border-gray-100 py-2 w-56 z-50">
          <div className="px-4 py-2 border-b border-gray-100">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Share via</p>
          </div>
          {shareOptions.map((option) => (
            <button
              key={option.name}
              onClick={(e) => {
                e.stopPropagation();
                option.action();
              }}
              className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors cursor-pointer"
            >
              <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${option.bg}`}>
                <i className={`${option.icon} ${option.color} text-base`}></i>
              </div>
              <span className="text-sm font-medium text-gray-700">{option.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
